import { format } from "date-fns";
import {
  type ByWeekday,
  type Frequency,
  RRule,
  rrulestr,
  Weekday
} from "rrule";
import {
  byWeekDay,
  getNthOccurrenceOfDayInMonth,
  RRULE_WEEKDAYS
} from "@/features/reserve/components/ReserveFieldRecurring/reserveRecurringUtils";

/**
 * The repeats a requester can pick straight from the select, and the RRULE
 * each one stands for. Anything else goes through "Custom", which edits the
 * rule's frequency, interval and weekdays directly.
 */
export type ReservationRepeat = "daily" | "weekly" | "monthly";

export const RESERVATION_REPEATS: ReservationRepeat[] = [
  "daily",
  "weekly",
  "monthly"
];

const ORDINALS = ["first", "second", "third", "fourth", "fifth"];

/**
 * The RRULE for a repeat, anchored on the day it starts from: a weekly rule
 * names that weekday, a monthly one that weekday's place in the month.
 *
 * Built with neither DTSTART nor UNTIL. Core takes the first day as
 * `start_date` and the last as `end_date`, so carrying them in the rule as
 * well would say the same thing twice — and say something different the moment
 * one of them is edited.
 */
export const buildReservationRRule = (
  repeat: ReservationRepeat,
  firstDate: Date
): string => {
  // No `interval`: it defaults to 1, and leaving it out keeps the stored rule
  // to the canonical `FREQ=WEEKLY;BYDAY=TU` that core's own examples use.
  const rule =
    repeat === "daily"
      ? new RRule({ freq: RRule.DAILY })
      : repeat === "weekly"
        ? new RRule({
            freq: RRule.WEEKLY,
            byweekday: [RRULE_WEEKDAYS[firstDate.getDay()]]
          })
        : new RRule({
            freq: RRule.MONTHLY,
            byweekday: byWeekDay(firstDate)
          });
  return rule.toString().replace(/^RRULE:/, "");
};

/** The parts of a rule the "Custom" editor works on. */
export interface ReservationRRuleOptions {
  freq: Frequency;
  interval: number;
  byweekday: Weekday[];
}

const toWeekday = (day: ByWeekday): Weekday =>
  day instanceof Weekday
    ? day
    : typeof day === "number"
      ? new Weekday(day)
      : Weekday.fromStr(day);

/**
 * A stored rule as the custom editor's three controls. A rule that can't be
 * read opens the editor on a plain weekly repeat rather than on nothing.
 */
export const reservationRRuleOptions = (
  rrule: string
): ReservationRRuleOptions => {
  const fallback: ReservationRRuleOptions = {
    freq: RRule.WEEKLY,
    interval: 1,
    byweekday: []
  };
  if (!rrule) return fallback;
  try {
    const { options, origOptions } = rrulestr(rrule);
    if (!origOptions) return fallback;
    const byweekday = origOptions.byweekday;
    return {
      freq: options.freq,
      interval: origOptions.interval ?? 1,
      byweekday: (Array.isArray(byweekday)
        ? byweekday
        : byweekday
          ? [byweekday]
          : []
      ).map(toWeekday)
    };
  } catch {
    return fallback;
  }
};

/**
 * The RRULE for what the custom editor holds. Like the presets it carries
 * neither DTSTART nor UNTIL, and an interval of 1 is left implicit so a custom
 * rule that happens to equal a preset is stored the same way.
 */
export const buildCustomReservationRRule = ({
  freq,
  interval,
  byweekday
}: ReservationRRuleOptions): string => {
  const rule = new RRule({
    freq,
    ...(interval > 1 ? { interval } : {}),
    ...(byweekday.length > 0 ? { byweekday } : {})
  });
  return rule.toString().replace(/^RRULE:/, "");
};

/** Parts `rrulestr` puts on every rule, whatever the string actually said. */
const PARSER_ADDED_OPTIONS = ["dtstart", "tzid"];

/**
 * Which of the three repeats a stored rule is, or `null` for anything else —
 * a rule written through the API, or one this UI stopped offering. The select
 * shows those as "Custom" and leaves them alone.
 *
 * Matching on the frequency alone is not enough: `FREQ=WEEKLY;BYDAY=MO,WE,FR`
 * and `FREQ=WEEKLY;COUNT=10` are weekly too, and calling them a preset lets
 * the re-anchoring below rebuild them as a bare one-weekday rule — a custom
 * series silently rewritten by an unrelated change to the days. So a rule only
 * counts as ours when it declares nothing this form wouldn't have written.
 *
 * The check is deliberately strict: mistaking ours for custom costs a select
 * reading "Custom repeat", while mistaking custom for ours destroys the rule.
 */
export const reservationRepeatOf = (
  rrule: string
): ReservationRepeat | null => {
  if (!rrule) return null;
  try {
    const { options, origOptions } = rrulestr(rrule);
    // A string carrying RDATE/EXDATE parses to a set, which has no options of
    // its own to inspect — and is certainly not one of three presets.
    if (!origOptions) return null;

    const declared = Object.keys(origOptions).filter(
      (option) => !PARSER_ADDED_OPTIONS.includes(option)
    );
    const byweekday = origOptions.byweekday;
    const weekdays = Array.isArray(byweekday)
      ? byweekday
      : byweekday
        ? [byweekday]
        : [];

    if (options.freq === RRule.DAILY) {
      return declared.length === 1 && declared[0] === "freq" ? "daily" : null;
    }

    const isFreqAndOneWeekday =
      declared.length === 2 &&
      declared.includes("freq") &&
      declared.includes("byweekday") &&
      weekdays.length === 1;
    if (!isFreqAndOneWeekday) return null;

    // The nth is what separates the two: a weekly rule names a weekday, a
    // monthly one names its place in the month.
    const hasNthOfMonth =
      typeof (weekdays[0] as { n?: number })?.n === "number";
    if (options.freq === RRule.WEEKLY) return hasNthOfMonth ? null : "weekly";
    if (options.freq === RRule.MONTHLY) return hasNthOfMonth ? "monthly" : null;
    return null;
  } catch {
    // A rule this UI can't read is still a rule: the caller keeps it as it is.
    return null;
  }
};

/** `Every week (Tuesday)`, naming what the picked day makes it repeat on. */
export const reservationRepeatLabel = (
  repeat: ReservationRepeat,
  firstDate: Date | undefined
): string => {
  if (repeat === "daily") return "Every day";
  if (!firstDate) return repeat === "weekly" ? "Every week" : "Every month";
  if (repeat === "weekly") {
    return `Every week (${format(firstDate, "EEEE")})`;
  }
  const ordinal = ORDINALS[getNthOccurrenceOfDayInMonth(firstDate) - 1];
  return ordinal
    ? `Every month (${ordinal} ${format(firstDate, "EEEE")})`
    : "Every month";
};

/**
 * The same repeat, rebuilt for a new first day.
 *
 * A weekly or monthly rule names the weekday it was made from, so moving the
 * days has to move the rule with them — otherwise a request that started on a
 * Tuesday keeps repeating on Tuesdays after the requester moves it to a
 * Thursday. A rule this UI doesn't recognise is left exactly as it is.
 */
export const reanchorReservationRRule = (
  rrule: string,
  firstDate: Date | undefined
): string => {
  const repeat = reservationRepeatOf(rrule);
  if (!repeat || !firstDate) return rrule;
  return buildReservationRRule(repeat, firstDate);
};

/**
 * The rule in words — "every week on Tuesday" — for showing a submitted answer
 * back. A rule that can't be read is shown as it was stored, which is at least
 * true.
 */
export const describeReservationRRule = (rrule: string): string => {
  try {
    return RRule.fromString(rrule).toText();
  } catch {
    return rrule;
  }
};
